import {
  Anchor,
  Card,
  Group,
  SegmentedControl,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { BarChart } from "@mantine/charts";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { formatTimezoneLabel } from "@mip-tg-bot-turntable/utils";
import { adminErrText, adminGet, AdminApiError } from "../api.ts";
import { AdminTable } from "../components/AdminTable.tsx";

interface AnalyticsOverview {
  readonly totalVisits: number;
  readonly attributedVisits: number;
  readonly cookieReuseVisits: number;
  readonly joinedClicks: number;
  readonly joinEvents?: number;
  readonly joinUsers?: number;
  readonly conversionsSent: number;
  readonly conversionsPending: number;
  readonly conversionsFailed: number;
  readonly conversionsSkipped: number;
  readonly joinRatePct: number | null;
  readonly reportRatePct: number | null;
  readonly skipRatePct?: number | null;
}

interface BotSummary {
  readonly botId: string;
  readonly botCode: string;
  readonly botName: string;
  readonly timezone: string;
  readonly statsDay: string;
  readonly overview: AnalyticsOverview;
}

interface AnalyticsOverviewResponse {
  readonly days: number;
  readonly totals: {
    readonly totalVisits: number;
    readonly attributedVisits: number;
    readonly joinedClicks: number;
    readonly conversionsSent: number;
    readonly conversionsSkipped?: number;
  };
  readonly bots: ReadonlyArray<BotSummary>;
}

const TREND_DAYS = [
  { label: "业务今日", value: "1" },
  { label: "近 7 业务日", value: "7" },
  { label: "近 14 业务日", value: "14" },
  { label: "近 30 业务日", value: "30" },
];

const fmtPct = (v: number | null): string => (v === null ? "-" : `${v}%`);

export function AnalyticsOverviewPage() {
  const [days, setDays] = useState("1");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["analytics", "stats", "overview", days],
    queryFn: () => adminGet<AnalyticsOverviewResponse>(`/analytics/stats/overview?days=${days}`),
    refetchInterval: 60_000,
  });

  const chartData =
    data?.bots.map((b) => ({
      bot: b.botName.length > 10 ? `${b.botName.slice(0, 10)}...` : b.botName,
      visits: b.overview.totalVisits,
      joined: b.overview.joinedClicks,
      sent: b.overview.conversionsSent,
      skipped: b.overview.conversionsSkipped,
    })) ?? [];

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Stack gap={4}>
          <Title order={2}>分析机器人报表</Title>
          <Text size="sm" c="dimmed">
            各机器人按自身业务时区切日汇总（默认 IST）。KPI 为事件数；浏览器本地日不参与切日。
            当前窗口：近 {days} 业务日。
          </Text>
        </Stack>
        <SegmentedControl value={days} onChange={setDays} data={TREND_DAYS} />
      </Group>

      {isLoading ? (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} h={80} radius="md" />
            ))}
          </SimpleGrid>
          <Skeleton h={300} radius="md" />
          <Skeleton h={200} radius="md" />
        </Stack>
      ) : isError ? (
        <Text c="red" size="sm" ta="center" py="lg">
          {adminErrText((error as AdminApiError).code)}
        </Text>
      ) : (
        <>
          <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="md">
            <KpiCard label="总访问(事件)" value={data?.totals.totalVisits ?? 0} />
            <KpiCard label="总归因访问(事件)" value={data?.totals.attributedVisits ?? 0} />
            <KpiCard label="总归因入群(事件)" value={data?.totals.joinedClicks ?? 0} />
            <KpiCard label="总已上报(事件)" value={data?.totals.conversionsSent ?? 0} />
            <KpiCard label="总上报跳过(事件)" value={data?.totals.conversionsSkipped ?? 0} />
          </SimpleGrid>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text fw={600} size="lg" mb="md">
              各机器人对比（近 {days} 天）
            </Text>
            {chartData.length === 0 ? (
              <Text c="dimmed" size="sm" ta="center" py="lg">
                暂无分析机器人
              </Text>
            ) : (
              <BarChart
                h={Math.max(240, chartData.length * 48)}
                data={chartData}
                dataKey="bot"
                withTooltip
                tickLine="y"
                series={[
                  { name: "visits", label: "访问(事件)", color: "violet.6" },
                  { name: "joined", label: "归因入群(事件)", color: "teal.6" },
                  { name: "sent", label: "已上报(事件)", color: "green.6" },
                  { name: "skipped", label: "上报跳过(事件)", color: "gray.6" },
                ]}
              />
            )}
          </Card>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text fw={600} size="lg" mb="md">
              机器人明细
            </Text>
            {(data?.bots.length ?? 0) === 0 ? (
              <Text c="dimmed" size="sm" ta="center" py="lg">
                暂无分析机器人，请先在机器人列表创建
              </Text>
            ) : (
              <AdminTable minWidth={960}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>名称</Table.Th>
                    <Table.Th>Code</Table.Th>
                    <Table.Th>业务时区</Table.Th>
                    <Table.Th>业务日</Table.Th>
                    <Table.Th>访问(事件)</Table.Th>
                    <Table.Th>归因访问</Table.Th>
                    <Table.Th>归因入群(事件)</Table.Th>
                    <Table.Th>入群用户</Table.Th>
                    <Table.Th>已上报(事件)</Table.Th>
                    <Table.Th>上报跳过</Table.Th>
                    <Table.Th>跳过率</Table.Th>
                    <Table.Th>入群率</Table.Th>
                    <Table.Th>上报率</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data?.bots.map((b) => (
                    <Table.Tr key={b.botId}>
                      <Table.Td>
                        <Anchor component={Link} to={`/bots/${b.botId}/overview`}>
                          {b.botName}
                        </Anchor>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {b.botCode}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{formatTimezoneLabel(b.timezone)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{b.statsDay}</Text>
                      </Table.Td>
                      <Table.Td>{b.overview.totalVisits.toLocaleString()}</Table.Td>
                      <Table.Td>{b.overview.attributedVisits.toLocaleString()}</Table.Td>
                      <Table.Td>
                        {(b.overview.joinEvents ?? b.overview.joinedClicks).toLocaleString()}
                      </Table.Td>
                      <Table.Td>{(b.overview.joinUsers ?? 0).toLocaleString()}</Table.Td>
                      <Table.Td>{b.overview.conversionsSent.toLocaleString()}</Table.Td>
                      <Table.Td>{b.overview.conversionsSkipped.toLocaleString()}</Table.Td>
                      <Table.Td>{fmtPct(b.overview.skipRatePct ?? null)}</Table.Td>
                      <Table.Td>{fmtPct(b.overview.joinRatePct)}</Table.Td>
                      <Table.Td>{fmtPct(b.overview.reportRatePct)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </AdminTable>
            )}
          </Card>
        </>
      )}
    </Stack>
  );
}

function KpiCard(props: { readonly label: string; readonly value: number }) {
  return (
    <Card shadow="xs" padding="md" radius="md" withBorder>
      <Text size="xs" c="dimmed" mb={4}>
        {props.label}
      </Text>
      <Title order={3}>{props.value.toLocaleString()}</Title>
    </Card>
  );
}
